import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Timer, Edit3, Check, X } from 'lucide-react';

interface WorkoutTimerCardProps {
  workoutName?: string;
  isWorkoutActive?: boolean;
  onTimerStart?: () => void;
  onTimerStop?: () => void;
}

const WorkoutTimerCard: React.FC<WorkoutTimerCardProps> = ({
  workoutName = 'HIIT Circuit',
  isWorkoutActive = false,
  onTimerStart,
  onTimerStop
}) => {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<'work' | 'rest'>('work');
  const [currentRound, setCurrentRound] = useState(1);
  const [workTime, setWorkTime] = useState(45);
  const [restTime, setRestTime] = useState(15);
  const [totalRounds] = useState(8);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPhase, setEditingPhase] = useState<'work' | 'rest' | null>(null);
  const [tempWorkTime, setTempWorkTime] = useState(workTime);
  const [tempRestTime, setTempRestTime] = useState(restTime);
  
  const lastTapTime = useRef(0);
  const tapTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning) {
      interval = setInterval(() => {
        setTime(prevTime => {
          const newTime = prevTime + 1;
          
          if (currentPhase === 'work' && newTime >= workTime) {
            setCurrentPhase('rest');
            return 0;
          } else if (currentPhase === 'rest' && newTime >= restTime) {
            if (currentRound < totalRounds) {
              setCurrentRound(prev => prev + 1);
              setCurrentPhase('work');
              return 0;
            } else {
              setIsRunning(false);
              return newTime;
            }
          }
          
          return newTime;
        });
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isRunning, currentPhase, currentRound, workTime, restTime, totalRounds]);

  useEffect(() => {
    if (isWorkoutActive && !isRunning) {
      setIsRunning(true);
      onTimerStart?.();
    }
  }, [isWorkoutActive, isRunning, onTimerStart]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseTime = (): number => {
    return currentPhase === 'work' ? workTime : restTime;
  };

  const getProgress = (): number => {
    return (time / getPhaseTime()) * 100;
  };

  const handlePlayPause = () => {
    const newRunningState = !isRunning;
    setIsRunning(newRunningState);
    
    if (newRunningState) {
      onTimerStart?.();
    } else {
      onTimerStop?.();
    }
  };

  const handleReset = () => {
    setTime(0);
    setIsRunning(false);
    setCurrentPhase('work');
    setCurrentRound(1);
    onTimerStop?.();
  };

  const handleDoubleTap = (phase: 'work' | 'rest') => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime.current;
    
    console.log(`Tap detected: timeSinceLastTap=${timeSinceLastTap}ms, phase=${phase}`);
    
    if (timeSinceLastTap < 500 && lastTapTime.current > 0) {
      if (tapTimeout.current) {
        clearTimeout(tapTimeout.current);
        tapTimeout.current = null;
      }
      
      console.log('Double-tap detected - opening editing interface');
      if (!isEditing) {
        setIsEditing(true);
        setEditingPhase(phase);
        setTempWorkTime(workTime);
        setTempRestTime(restTime);
      }
      lastTapTime.current = 0;
    } else {
      lastTapTime.current = now;
      console.log('First tap registered, waiting for second tap');
      
      if (tapTimeout.current) {
        clearTimeout(tapTimeout.current);
      }
      
      tapTimeout.current = setTimeout(() => {
        lastTapTime.current = 0;
        tapTimeout.current = null;
        console.log('Double-tap timeout - resetting');
      }, 500);
    }
  };

  const handleSaveEdit = () => {
    console.log('Save button clicked - updating timer settings');
    console.log(`Current values: Work=${tempWorkTime}s, Rest=${tempRestTime}s`);
    
    setWorkTime(tempWorkTime);
    setRestTime(tempRestTime);
    setIsEditing(false);
    setEditingPhase(null);
    
    if (isRunning) {
      setTime(0);
      setCurrentPhase('work');
    }
    
    console.log(`Timer updated successfully: Work=${tempWorkTime}s, Rest=${tempRestTime}s`);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingPhase(null);
    setTempWorkTime(workTime);
    setTempRestTime(restTime);
  };

  return (
    <div className="sculptd-card-elevated sculptd-sheen sculptd-container-padding" style={{
      boxShadow: 'var(--shadow-card-separation)',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <div className="text-center space-y-6">
        <div className="flex items-center justify-center space-x-3">
          <Timer className="h-6 w-6" style={{ color: 'var(--color-primary)' }} />
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
            {workoutName}
          </h2>
        </div>

        <div className="space-y-4">
          <div 
            className="text-6xl font-bold cursor-pointer select-none"
            style={{ 
              color: currentPhase === 'work' ? 'var(--color-teal)' : 'var(--color-lavender)'
            }}
            onClick={() => handleDoubleTap(currentPhase)}
          >
            {formatTime(getPhaseTime() - time)}
          </div>
          
          <div className="space-y-3">
            <div 
              className="text-xl font-semibold uppercase tracking-wide cursor-pointer select-none"
              style={{ 
                color: currentPhase === 'work' ? 'var(--color-teal)' : 'var(--color-lavender)'
              }}
              onClick={() => handleDoubleTap(currentPhase)}
            >
              {currentPhase === 'work' ? 'WORK' : 'REST'}
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="h-3 rounded-full transition-all duration-1000 ease-linear"
                style={{
                  width: `${getProgress()}%`,
                  background: currentPhase === 'work' 
                    ? 'var(--color-teal)' 
                    : 'var(--color-lavender)'
                }}
              />
            </div>
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-4 p-4 rounded-lg" style={{ background: 'var(--color-background)', border: '1px solid var(--color-border)' }}>
            <div className="text-lg font-semibold text-center" style={{ color: 'var(--color-text)' }}>
              Edit Timer Settings
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Work Time (seconds)
                </label>
                <input
                  type="number"
                  min="5"
                  max="300"
                  value={tempWorkTime}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setTempWorkTime(45);
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue) && numValue >= 5 && numValue <= 300) {
                        setTempWorkTime(numValue);
                      }
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  className="w-full p-2 rounded border text-center"
                  style={{ 
                    background: 'var(--color-white)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)',
                    minHeight: '44px',
                    padding: '8px 16px'
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Rest Time (seconds)
                </label>
                <input
                  type="number"
                  min="5"
                  max="300"
                  value={tempRestTime}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setTempRestTime(15);
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue) && numValue >= 5 && numValue <= 300) {
                        setTempRestTime(numValue);
                      }
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  className="w-full p-2 rounded border text-center"
                  style={{ 
                    background: 'var(--color-white)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)',
                    minHeight: '44px',
                    padding: '8px 16px'
                  }}
                />
              </div>
            </div>
            
            <div className="flex justify-center space-x-3">
              <button
                onClick={handleSaveEdit}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer"
                style={{
                  background: 'var(--color-teal)',
                  color: 'var(--color-white)',
                  boxShadow: 'var(--shadow-md)',
                  minHeight: '44px',
                  padding: '8px 16px',
                  border: 'none',
                  outline: 'none',
                  pointerEvents: 'auto',
                  zIndex: 10
                }}
              >
                <Check className="h-4 w-4" />
                <span>Save</span>
              </button>
              
              <button
                onClick={handleCancelEdit}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer"
                style={{
                  background: 'var(--color-background)',
                  color: 'var(--color-text-muted)',
                  boxShadow: 'var(--shadow-md)',
                  border: '1px solid var(--color-border)',
                  outline: 'none',
                  pointerEvents: 'auto',
                  zIndex: 10
                }}
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center space-x-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span>Round {currentRound} of {totalRounds}</span>
            <span>•</span>
            <span>{workTime}s work / {restTime}s rest</span>
            <span>•</span>
            <span className="text-xs opacity-75">Double-tap timer to edit</span>
          </div>
        )}

        <div className="flex justify-center space-x-4">
          <button
            onClick={handlePlayPause}
            className="p-4 rounded-full transition-all duration-200"
            style={{
              background: 'var(--color-teal)',
              color: 'var(--color-white)',
              boxShadow: 'var(--shadow-lg)'
            }}
          >
            {isRunning ? (
              <Pause className="h-8 w-8" />
            ) : (
              <Play className="h-8 w-8" />
            )}
          </button>
          
          <button
            onClick={handleReset}
            className="p-4 rounded-full transition-all duration-200"
            style={{
              background: 'var(--color-background)',
              color: 'var(--color-text-muted)',
              boxShadow: 'var(--shadow-md)',
              border: '1px solid var(--color-border)'
            }}
          >
            <RotateCcw className="h-8 w-8" />
          </button>
        </div>

        {currentRound === totalRounds && time >= getPhaseTime() && (
          <div 
            className="p-4 rounded-lg"
            style={{ 
              background: 'var(--color-teal)',
              color: 'var(--color-white)'
            }}
          >
            <p className="font-semibold">Workout Complete!</p>
            <p className="text-sm opacity-90">Great job finishing your HIIT circuit!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkoutTimerCard;
